import type { Messages } from '@lingui/core';
import { i18n } from '@lingui/core';
export { i18n };

export const SUPPORTED_LOCALES = ['en', 'pl', 'no', 'ja'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

/** Defaults & storage key */
const DEFAULT_LOCALE: AppLocale = 'en';
const LOCALE_STORAGE_KEY = 'locale';

/**
 * Dynamic catalog loaders (Vite splits these into separate chunks).
 * NOTE: keeping the ".ts" extension since you already use it — both forms work with Vite.
 */
const localeCatalogLoaders = {
    en: () => import('@locales/en/messages.ts'),
    pl: () => import('@locales/pl/messages.ts'),
    no: () => import('@locales/no/messages.ts'),
    ja: () => import('@locales/ja/messages.ts'),
} satisfies Record<AppLocale, () => Promise<{ messages: Messages }>>;

/**
 * Load and activate a locale.
 * - Accepts a supported locale code; falls back to DEFAULT_LOCALE if not.
 * - Loads the compiled catalog and activates it in Lingui runtime.
 */
export const activateLocale = async (requestedLocale: AppLocale) => {
    const resolvedLocale = (SUPPORTED_LOCALES as readonly string[]).includes(requestedLocale)
        ? requestedLocale
        : DEFAULT_LOCALE;

    const catalogModule = await localeCatalogLoaders[resolvedLocale]();
    // Either form is fine; this one is explicit
    i18n.load(resolvedLocale, catalogModule.messages);
    i18n.activate(resolvedLocale);
};

export const persistLocale = (locale: AppLocale) => {
    try {
        if (typeof window !== 'undefined') {
            localStorage.setItem(LOCALE_STORAGE_KEY, locale);
        }
    } catch {
        // Ignore storage errors in restricted browser/test environments.
    }
};

/**
 * Decide initial locale on app start:
 * 1) try persisted value from localStorage
 * 2) else try browser preference (navigator.languages[0] or navigator.language)
 * 3) else DEFAULT_LOCALE
 *
 * Includes a safe guard for environments where navigator/localStorage may be undefined
 * (SSR, tests, some webviews), and for odd language tags (uses [-_] split and optional chaining).
 */
export const pickInitialLocale = (): AppLocale => {
    // 1) stored locale (guard against storage access errors)
    let storedLocale: AppLocale | null = null;
    try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem(LOCALE_STORAGE_KEY) : null;
        if (raw && (SUPPORTED_LOCALES as readonly string[]).includes(raw)) {
            storedLocale = raw as AppLocale;
        }
    } catch {
        // ignore storage errors and continue to browser detection
    }
    if (storedLocale) return storedLocale;

    // 2) browser preferred locale (handles both "en-US" and "en_US")
    const browserNavigator = typeof navigator !== 'undefined' ? navigator : undefined;
    const primaryTag =
        Array.isArray(browserNavigator?.languages) && browserNavigator.languages.length > 0
            ? browserNavigator.languages[0]
            : browserNavigator?.language;

    const browserCode = typeof primaryTag === 'string' ? primaryTag.split?.(/[-_]/)?.[0] : undefined;

    if (browserCode && (SUPPORTED_LOCALES as readonly string[]).includes(browserCode)) {
        return browserCode as AppLocale;
    }

    return DEFAULT_LOCALE;
};
